// Per-item colour for a BatchedMesh.
//
// three 0.164's BatchedMesh has matrices and visibility per item but no
// colour: setColorAt arrived upstream later. The viewer needs it,
// because selection, the ghosted "disabled" bucket and the revision
// overlay all speak through InstanceStateApplicator's per-instance
// colour write, and an item that cannot take a colour cannot look
// selected.
//
// So the same trick BatchedMesh already uses for matrices is used for
// colour: a float texture indexed by the item's `batchId` attribute,
// sampled in the vertex shader and multiplied into vColor. The
// fragments carry their real colour per vertex (COLOR_0), so this is a
// tint on top, which is exactly what the applicator writes.
//
// The material is patched once per batch, since the texture is
// per-batch. The program is shared: the cache key is the same for every
// patched material, so this costs uniforms, not shader compiles.
import { Color, DataTexture, FloatType, RGBAFormat } from 'three';
import { markSceneChanged } from './renderSignal';
import { GLSL_APPLY_STATE_COLOR } from './stateColor';
import { SLOT_GHOST_ALPHA, slotHalftoneFrag } from './ghostMaterial';

const DECL = `
uniform highp sampler2D batchTintTexture;
vec4 getBatchTint( const in float i ) {
  int size = textureSize( batchTintTexture, 0 ).x;
  int j = int( i );
  int x = j % size;
  int y = j / size;
  return texelFetch( batchTintTexture, ivec2( x, y ), 0 );
}
varying float vBatchGhost;
`;
const FRAG_DECL = 'varying float vBatchGhost;\n';

const APPLY = `
vBatchGhost = 0.0;
#ifdef USE_BATCHING
  vec4 batchTint = getBatchTint( batchId );
  vColor.rgb = blApplyStateColor( vColor.rgb, batchTint.rgb );
  vBatchGhost = batchTint.a > 1.5 ? 1.0 : 0.0;
#endif
`;

// One RGBA float texel per item, initialised to white so an unwritten
// item renders exactly as its vertex colours say.
function makeTintTexture(maxItems) {
  let size = Math.ceil(Math.sqrt(Math.max(1, maxItems)));
  size = Math.max(4, size);
  const data = new Float32Array(size * size * 4);
  data.fill(1);
  const texture = new DataTexture(data, size, size, RGBAFormat, FloatType);
  texture.needsUpdate = true;
  markSceneChanged('batchColor');
  return texture;
}

// Patch a material clone to sample the tint texture. Returns the
// texture so the batch can write into it.
export function attachBatchTint(material, maxItems) {
  const texture = makeTintTexture(maxItems);
  material.userData = material.userData || {};
  material.userData.__batchTint = texture;

  const previous = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    if (typeof previous === 'function') previous(shader, renderer);
    shader.uniforms.batchTintTexture = { value: texture };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n' + GLSL_APPLY_STATE_COLOR + DECL)
      // color_vertex is where vColor is built from the geometry's own
      // colour attribute; the tint multiplies what it produced.
      .replace('#include <color_vertex>', '#include <color_vertex>\n' + APPLY);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + FRAG_DECL)
      .replace('#include <color_fragment>', '#include <color_fragment>\n' + slotHalftoneFrag('vBatchGhost'));
  };
  const prevKey = typeof material.customProgramCacheKey === 'function'
    ? material.customProgramCacheKey.bind(material) : null;
  material.customProgramCacheKey = () => (prevKey ? prevKey() : '') + '|batchTint2';
  material.needsUpdate = true;
  return texture;
}

const _tmpColor = new Color();

// Write one item's tint. Mirrors InstancedMesh.setColorAt's signature so
// the applicator can call it without knowing which kind of mesh it has.
export function setBatchTintAt(mesh, id, color) {
  const texture = mesh && mesh.material && mesh.material.userData
    && mesh.material.userData.__batchTint;
  if (!texture) return;
  const data = texture.image.data;
  const offset = id * 4;
  if (offset + 3 >= data.length) return;
  const c = color || _tmpColor.setRGB(1, 1, 1);
  if (data[offset] === c.r && data[offset + 1] === c.g && data[offset + 2] === c.b) return;
  data[offset] = c.r;
  data[offset + 1] = c.g;
  data[offset + 2] = c.b;
  // alpha is the ghost flag (setBatchGhostAt), left as it is.
  texture.needsUpdate = true;
  markSceneChanged('batchColor');
}

// Ghost one item (drawn as a halftone) or give it back its solid look.
export function setBatchGhostAt(mesh, id, ghosted) {
  const texture = mesh && mesh.material && mesh.material.userData
    && mesh.material.userData.__batchTint;
  if (!texture) return;
  const data = texture.image.data;
  const offset = id * 4 + 3;
  if (offset >= data.length) return;
  const v = ghosted ? SLOT_GHOST_ALPHA : 1;
  if (data[offset] === v) return;
  data[offset] = v;
  texture.needsUpdate = true;
  markSceneChanged('batchColor');
}

export function disposeBatchTint(material) {
  const texture = material && material.userData && material.userData.__batchTint;
  if (texture) texture.dispose();
  if (material && material.userData) material.userData.__batchTint = null;
}
